import { useMemo, useState, useRef, useContext } from 'react';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import KizenTypography from 'app/kizentypo';
import { colorsButton } from 'app/colors';
import IconButtonMenu from 'components/Kizen/IconButtonMenu';
import { IconSizing } from 'components/Kizen/Icon';
import InlineTableFieldInput from 'components/Fields/InlineTableFieldInput';
import BubbleProfileImage from 'components/Charts/ScheduledActivities/BubbleProfileImage';
import { DragIcon, BubbleWrapper } from 'components/Board/BaseCard';
import DateInputInline from 'components/Inputs/inline/DateInput';
import { DATE_EMPTY_FORMAT } from 'components/Inputs/DateInput';
import BasicModal from 'components/Modals/presets/BasicModal';
import useModal from 'components/Modals/useModal';
import PipelineService from 'services/PipelineService';
import {
  API_DATE_FORMAT,
  snakeToCamelCase,
  snakeToCamelCaseKeys,
} from 'services/helpers';
import { STAGE_STATUSES } from 'utility/constants';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import ArchiveModal from 'components/Modals/ArchiveModal';
import { ApplySelectOverlayButton } from 'components/Inputs/SelectOverlay';
import ClearSelectButton from 'components/Inputs/Select/ClearButton';
import { PluginContext } from 'ts-components/Plugins/context';
import {
  Entities,
  useSelectTypeaheadWithScroll,
} from 'components/Inputs/Select/hooks';
import { convertToTeamOption } from 'utility/TransformToSelectOptions';
import {
  getPercentageChanceToCloseProps,
  getMenuOptions,
  getPatchData,
} from './helpers';
import { isSummarizedValue } from 'components/Fields/helpers';
import {
  TitleWrapper,
  StyledValidatedField,
  StyledIconMenu,
  StyledDateWrapper,
  StyledDateInputInlineSelect,
  StyledSelect,
  StyledBaseCard,
  ContentWrapper,
  StageSelect,
  StyledSelectInline,
} from './styles';
import { TimeInStage } from './TimeInStage';
import { useDateTooltip } from 'components/Inputs/hooks/useDateTooltip';
import { isStageField } from 'checks/fields';
const specialStageStatuses = [
  STAGE_STATUSES.won,
  STAGE_STATUSES.lost,
  STAGE_STATUSES.disqualified,
];
import { DEFAULT_FIELD_NAMES } from 'utility/constants';
import { WithFieldContext } from './DesktopLayout/FieldsContextProvider';

const ActualCloseDate = ({ date }) => {
  const { t } = useTranslation();
  const [dateTooltipProps, dateTooltip] = useDateTooltip({
    date,
    predicate: t('Actual Close Date'),
  });

  return (
    <StyledDateWrapper {...dateTooltipProps}>
      {dateTooltip}
      <KizenTypography type="small">
        {date ? format(date, 'MM/dd/yyyy') : DATE_EMPTY_FORMAT}
      </KizenTypography>
    </StyledDateWrapper>
  );
};

const Field = (props) => {
  const {
    data,
    fields,
    handleUpdateBoardViewRecords,
    handleUpdateFieldOption,
    item,
    model,
    onChangeStage,
    onSubmitRecord,
    rootCloseEvent,
    selectOverlayObserverProps,
    settings,
  } = props;

  const { t } = useTranslation();
  const { dataAdornments } = useContext(PluginContext);

  if (item.name === DEFAULT_FIELD_NAMES.name) {
    if (!data?.access.edit) {
      return (
        <TextEllipsisWithTooltip
          type="link"
          to={`/custom-objects/${model.id}/${data.id}/details`}
        >
          {data.name}
        </TextEllipsisWithTooltip>
      );
    }

    return (
      <StyledValidatedField
        type="link"
        to={`/custom-objects/${model.id}/${data.id}/details`}
        value={data.name}
        position="unset"
        inModal
        onSubmit={async (name) => {
          await onSubmitRecord(data.id, { name });
        }}
        onFocus={(ev) => ev.stopPropagation()}
        fieldId="name"
      />
    );
  }

  if (item.name === DEFAULT_FIELD_NAMES.displayName) {
    return (
      <TextEllipsisWithTooltip
        type="link"
        to={`/custom-objects/${model.id}/${data.id}/details`}
      >
        {data.displayName}
      </TextEllipsisWithTooltip>
    );
  }

  const fieldInputProps = {
    ...getPercentageChanceToCloseProps(item, fields, data, t),
    adornments: dataAdornments,
    contentWidth: (settings.columnWidth || 270) - 44,
    displayMaskedValue: true,
    fetchUrl: `custom-objects/${model.id}`,
    field: item,
    handleUpdateFieldOption,
    handleUpdateTableRecords: handleUpdateBoardViewRecords,
    isCustomObjects: true,
    object: data,
    objectId: model.id,
    rootCloseEvent,
    serviceToUse: PipelineService,
    selectOverlayObserverProps,
    showDisplayNameTooltip: true,
  };

  if (isStageField(item)) {
    return (
      <WithFieldContext>
        {({ setMenuIsOpen }) => (
          <InlineTableFieldInput
            {...fieldInputProps}
            onSetShow={setMenuIsOpen}
            menuLeftButton={false}
            value={data[snakeToCamelCase(item.name)]}
            onSubmit={async (patch) => {
              await onChangeStage(data, { stageId: patch.stage });
            }}
          />
        )}
      </WithFieldContext>
    );
  }

  if (item.isDefault) {
    return (
      <WithFieldContext>
        {({ setMenuIsOpen }) => (
          <InlineTableFieldInput
            {...fieldInputProps}
            onSetShow={setMenuIsOpen}
            value={data[snakeToCamelCase(item.name)]}
            onSubmit={async (patch) => {
              await onSubmitRecord(
                data.id,
                snakeToCamelCaseKeys(getPatchData(patch, item))
              );
            }}
          />
        )}
      </WithFieldContext>
    );
  }

  return (
    <WithFieldContext>
      {({ setMenuIsOpen }) => (
        <InlineTableFieldInput
          {...fieldInputProps}
          onSetShow={setMenuIsOpen}
          onSubmit={async (patch) => {
            const [key, value] = Object.entries(patch)[0];

            const payload = isSummarizedValue(value)
              ? {
                  addValues: (value.addValues || []).map(({ id }) => id),
                  removeValues: (value.removeValues || []).map(({ id }) => id),
                }
              : { value };

            await onSubmitRecord(data.id, {
              fields: [{ id: key, ...payload }],
            });
          }}
        />
      )}
    </WithFieldContext>
  );
};

const Card = ({
  data,
  model,
  stageStatus,
  settings,
  fields,
  stages,
  dragging,
  isDragItem,
  onChoseMenu,
  onSubmitRecord,
  onChangeStage,
  hideDnDIcon = false,
  handleUpdateFieldOption,
  selectOverlayObserverProps,
  rootCloseEvent,
  handleUpdateBoardViewRecords,
  ...props
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const ownerSelectRef = useRef();
  const [chosenStage, setStage] = useState(null);
  const [bringToTop, setBringToTop] = useState(false);
  const [forceToTop, setForceToTop] = useState(false);
  const disabledDragCardIds = useSelector(
    ({ recordsPage }) => recordsPage.boardData.disabledDragCardIds
  );
  const isDisabledDrag = useMemo(
    () => disabledDragCardIds.some((el) => el === data.id),
    [data.id, disabledDragCardIds]
  );

  const [selectProps] = useSelectTypeaheadWithScroll({
    selectRef: ownerSelectRef,
    objectToOption: convertToTeamOption,
    entity: Entities.TeamMember,
    alwaysOpen: true,
  });

  const closeDate = useMemo(() => {
    return specialStageStatuses.includes(stageStatus)
      ? data.actualCloseDate
      : data.estimatedCloseDate;
  }, [stageStatus, data.actualCloseDate, data.estimatedCloseDate]);

  const [topField = null, ...fieldsInCard] = useMemo(() => {
    return settings.fields
      .map((item) => fields.find((el) => el.id === item.value))
      .filter(Boolean);
  }, [settings.fields, fields]);

  const handleChangeStage = async () => {
    await onChangeStage(data, { stageId: chosenStage.value });
  };

  const handleDeleteRecord = () => {
    onChoseMenu('archive', data);
  };

  const [changeStageModalProps, , changeStageModal] = useModal({
    handleSubmit: handleChangeStage,
    handleHide: () => {
      setStage(null);
    },
  });
  const [archiveModalProps, , archiveModal] = useModal({
    handleSubmit: handleDeleteRecord,
  });

  const handleChoseMenuOption = (opt) => {
    switch (opt.value) {
      case 'changeStage': {
        changeStageModal.show();
        break;
      }
      case 'archive': {
        archiveModal.show();
        break;
      }
      case 'viewRecord': {
        history.push(`/custom-objects/${model.id}/${data.id}/details`);
        break;
      }
      default: {
        onChoseMenu(opt.value, data);
      }
    }
  };

  return (
    <>
      <StyledBaseCard
        isSelected={data.isSelected}
        bringToTop={bringToTop}
        forceToTop={forceToTop}
        isDragging={dragging}
        isDragItem={isDragItem}
        onFocus={() => setBringToTop(true)}
        onBlur={() => setBringToTop(false)}
        title={
          <>
            <TitleWrapper>
              {hideDnDIcon ? null : (
                <WithFieldContext>
                  {({ menuIsOpen }) => (
                    <IconSizing
                      size="15px"
                      className={classNames({
                        CardHandle: !isDisabledDrag && !menuIsOpen,
                      })}
                      style={{
                        cursor: isDisabledDrag
                          ? 'wait'
                          : menuIsOpen
                            ? 'auto'
                            : 'grab',
                      }}
                    >
                      <DragIcon
                        icon="reorder"
                        disabled={isDisabledDrag || menuIsOpen}
                      />
                    </IconSizing>
                  )}
                </WithFieldContext>
              )}
              {topField && (
                <Field
                  data={data}
                  fields={fields}
                  handleUpdateBoardViewRecords={handleUpdateBoardViewRecords}
                  handleUpdateFieldOption={handleUpdateFieldOption}
                  item={topField}
                  model={model}
                  onChangeStage={onChangeStage}
                  onSubmitRecord={onSubmitRecord}
                  rootCloseEvent={rootCloseEvent}
                  selectOverlayObserverProps={selectOverlayObserverProps}
                  settings={settings}
                />
              )}
            </TitleWrapper>
            <IconButtonMenu
              position="right"
              sizing="dense"
              title={t('Edit Record')}
              onOpen={() => setForceToTop(true)}
              onClose={() => setForceToTop(false)}
              color={colorsButton.iconGray}
              onChange={handleChoseMenuOption}
              options={getMenuOptions(!hideDnDIcon, data.access.remove, t)}
            >
              <StyledIconMenu icon="three-dot" />
            </IconButtonMenu>
          </>
        }
        footer={
          (settings.estimatedCloseDateVisible ||
            settings.timeInStageVisible ||
            settings.ownerVisible) && (
            <>
              <div>
                {settings.estimatedCloseDateVisible ? (
                  specialStageStatuses.includes(stageStatus) ? (
                    <ActualCloseDate date={closeDate} />
                  ) : (
                    <WithFieldContext>
                      {({ setMenuIsOpen }) => (
                        <DateInputInline
                          data-qa="estimated-close-date"
                          value={closeDate}
                          icon={null}
                          showToolTip
                          tooltipLabel={t('Estimated Close Date')}
                          popperConfig={{}}
                          className="labeled_element"
                          labelEl
                          onSubmit={async (value) => {
                            await onSubmitRecord(data.id, {
                              estimatedCloseDate: value
                                ? format(value, API_DATE_FORMAT)
                                : value,
                            });
                          }}
                          compactAlignment
                          rootCloseEvent={rootCloseEvent}
                          onSetShow={setMenuIsOpen}
                        >
                          <StyledDateInputInlineSelect
                            value={closeDate}
                            menuRightButton={<ApplySelectOverlayButton />}
                            menuLeftButton={<ClearSelectButton />}
                          />
                        </DateInputInline>
                      )}
                    </WithFieldContext>
                  )
                ) : null}
                {settings.timeInStageVisible ? (
                  <TimeInStage time={data.timeOnStage} />
                ) : null}
              </div>
              {settings.ownerVisible && data.owner && (
                <WithFieldContext>
                  {({ setMenuIsOpen }) => (
                    <StyledSelectInline
                      icon={null}
                      labelEl={
                        <BubbleWrapper>
                          <BubbleProfileImage
                            fullName={data.owner.fullName}
                            imageURL={data.owner.picture?.thumbnailUrl}
                            showToolTip
                            popperConfig={{
                              modifiers: {
                                preventOverflow: { enabled: false },
                              },
                            }}
                          />
                        </BubbleWrapper>
                      }
                      rootCloseEvent={rootCloseEvent}
                      onSetShow={setMenuIsOpen}
                    >
                      <StyledSelect
                        ref={ownerSelectRef}
                        value={data.owner}
                        onChange={(value) => {
                          onSubmitRecord(data.id, { ownerId: value.value });
                        }}
                        {...selectProps}
                      />
                    </StyledSelectInline>
                  )}
                </WithFieldContext>
              )}
            </>
          )
        }
        {...props}
      >
        {Boolean(fieldsInCard.length) && (
          <ContentWrapper onFocus={(ev) => ev.stopPropagation()}>
            {fieldsInCard.map((item) => {
              const fieldDisabled = !(
                !item.isReadOnly &&
                item.access?.edit &&
                data?.access.edit
              );
              return (
                <div
                  key={item.id}
                  data-qa-field={item.id}
                  data-qa-disabled={fieldDisabled}
                >
                  <Field
                    data={data}
                    fields={fields}
                    handleUpdateBoardViewRecords={handleUpdateBoardViewRecords}
                    handleUpdateFieldOption={handleUpdateFieldOption}
                    item={item}
                    model={model}
                    onChangeStage={onChangeStage}
                    onSubmitRecord={onSubmitRecord}
                    rootCloseEvent={rootCloseEvent}
                    selectOverlayObserverProps={selectOverlayObserverProps}
                    settings={settings}
                  />
                </div>
              );
            })}
          </ContentWrapper>
        )}
      </StyledBaseCard>
      <BasicModal
        heading={t('Move Record to Stage')}
        buttonText={t('Save')}
        disabled={!chosenStage}
        leftBtn={null}
        {...changeStageModalProps}
      >
        <StageSelect
          label={t('Choose Stage')}
          placeholder={t('Choose Stage')}
          value={chosenStage}
          options={stages}
          onChange={setStage}
          menuInline
        />
      </BasicModal>
      <ArchiveModal {...archiveModalProps}>
        {t('Custom Object Archive Modal body', {
          name: data?.name || 'entity record',
        })}
      </ArchiveModal>
    </>
  );
};

export default Card;
